import React from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Search, Download, Share2, Loader2, MapPin, ChevronLeft, ChevronRight, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import api from "../lib/api";
import { imageFor } from "../lib/thrones";
import { Reveal, useSEO } from "../components/site/Reveal";
import ReelDownloadButton from "../components/site/ReelDownloadButton";
import Newsletter from "../components/site/Newsletter";

const fold = (s) =>
  (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function RoyalTitles() {
  const navigate = useNavigate();
  useSEO(
    "Yorùbá Royal Titles (Oyè Ọba) — Kings & Kingdoms",
    "Explore the major royal titles of Yorùbáland — from the Ọọ̀ni of Ifẹ̀ and the Aláàfin of Ọ̀yọ́ to the crowned rulers of Èkìtì, Ìjẹ̀bú, Ẹ̀gbá, Oǹdó and beyond — grouped by region with the significance of each throne."
  );

  const [titles, setTitles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [term, setTerm] = React.useState("");
  const [region, setRegion] = React.useState("all");
  const [slide, setSlide] = React.useState(0);
  const [paused, setPaused] = React.useState(false);

  const [shareTarget, setShareTarget] = React.useState(null);
  const [modalTarget, setModalTarget] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    api
      .get("/royal-titles")
      .then((r) => setTitles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const regions = React.useMemo(() => {
    const seen = [];
    titles.forEach((t) => {
      if (t.region && !seen.includes(t.region)) seen.push(t.region);
    });
    return ["all", ...seen];
  }, [titles]);

  const filtered = titles.filter((t) => {
    if (region !== "all" && t.region !== region) return false;
    if (term.trim()) {
      const q = fold(term.trim());
      return (
        fold(t.town).includes(q) ||
        fold(t.title).includes(q) ||
        fold(t.note).includes(q) ||
        fold(t.region).includes(q)
      );
    }
    return true;
  });

  // group by region preserving seed order
  const grouped = [];
  filtered.forEach((t) => {
    let g = grouped.find((x) => x.region === t.region);
    if (!g) {
      g = { region: t.region, items: [] };
      grouped.push(g);
    }
    g.items.push(t);
  });

  // slideshow index (clamped to current filter) + navigation
  const slideIdx = filtered.length ? Math.min(slide, filtered.length - 1) : 0;
  const current = filtered[slideIdx];
  const prevSlide = () => setSlide(() => (slideIdx - 1 + filtered.length) % filtered.length);
  const nextSlide = () => setSlide(() => (slideIdx + 1) % filtered.length);

  // reset to first slide whenever the filter changes
  React.useEffect(() => {
    setSlide(0);
  }, [region, term]);

  // autoplay (pauses on hover)
  React.useEffect(() => {
    if (paused || filtered.length <= 1) return undefined;
    const id = setInterval(() => setSlide((s) => (s + 1) % filtered.length), 5000);
    return () => clearInterval(id);
  }, [paused, filtered.length]);

  const generatePng = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#12100e" });
  };

  const withCard = async (t, action) => {
    setShareTarget(t);
    setBusy(`${action}-${t.id}`);
    await new Promise((res) => setTimeout(res, 80));
    try {
      const url = await generatePng();
      if (!url) return;
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${t.title}.png`;
        a.click();
      } else {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], `${t.title}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `${t.title}`,
            text: `${t.title} — ${t.town} · via ÀsÀNigHT Worldwide`,
          });
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = `${t.title}.png`;
          a.click();
        }
      }
    } catch (e) {
      console.warn("Share card could not be generated or was cancelled:", e);
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="bg-night text-cream min-h-screen">
      {/* HERO */}
      <header className="max-w-5xl mx-auto px-6 pt-24 pb-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Yorùbá Royal Titles · Oyè Ọba</p>
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="royal-titles-title">
            Kings, Crowns & Kingdoms
          </h1>
          <p className="text-cream/70 text-lg leading-relaxed max-w-2xl">
            From the sacred Ọọ̀ni of Ifẹ̀ and the imperial Aláàfin of Ọ̀yọ́ to the crowned
            rulers of Èkìtì, Ìjẹ̀bú, Ẹ̀gbá and Oǹdó — explore the great thrones of Yorùbáland,
            grouped by region.
          </p>
          <div className="mt-6">
            <ReelDownloadButton section="royal-titles" label="Download Reel (MP4)" />
          </div>
        </Reveal>
      </header>

      {/* 16:9 SLIDESHOW */}
      {!loading && filtered.length > 0 && current && (
        <div className="max-w-5xl mx-auto px-6 pb-10">
          <Reveal>
            <div
              className="relative rounded-3xl overflow-hidden border border-gold/25 bg-night-surface"
              data-testid="royal-slideshow"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="relative aspect-video">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.55, ease: "easeOut" }}
                    className="absolute inset-0 cursor-pointer"
                    style={{ background: "#12100e" }}
                    data-testid="royal-slideshow-slide"
                    onClick={() => setModalTarget(current)}
                    role="button"
                    aria-label={`View details for ${current.title}`}
                  >
                    <img
                      src={current.image || imageFor(current.region)}
                      alt={`${current.title} royal regalia`}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(90deg, rgba(15,20,15,0.97) 0%, rgba(15,20,15,0.88) 40%, rgba(15,20,15,0.35) 78%, rgba(15,20,15,0.15) 100%)" }}
                    />
                    <div className="relative h-full flex flex-col justify-center px-8 md:px-16 py-10">
                      <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-gold/15 text-gold text-xs font-semibold px-3 py-1 mb-5">
                        <MapPin className="w-3.5 h-3.5" /> {current.region}
                      </span>
                      <p className="uppercase tracking-[0.25em] text-xs md:text-sm text-gold-soft mb-3">{current.town}</p>
                      <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl text-gold leading-[1.05] mb-4 max-w-3xl">{current.title}</h2>
                      {current.note && (
                        <p className="text-cream/75 text-base md:text-lg leading-relaxed max-w-2xl line-clamp-3">{current.note}</p>
                      )}
                      <span className="mt-6 inline-flex items-center gap-2 self-start text-gold text-sm font-semibold">
                        View details <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </motion.div>
                </AnimatePresence>

                <button onClick={(e) => { e.stopPropagation(); prevSlide(); }} data-testid="royal-slideshow-prev" aria-label="Previous throne" className="absolute left-4 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-night/60 border border-cream/20 text-cream hover:bg-gold hover:text-night transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); nextSlide(); }} data-testid="royal-slideshow-next" aria-label="Next throne" className="absolute right-4 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-night/60 border border-cream/20 text-cream hover:bg-gold hover:text-night transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>

                <div className="absolute bottom-4 left-8 md:left-16">
                  <span className="text-sm text-cream/70 font-semibold" data-testid="royal-slideshow-counter">{slideIdx + 1} / {filtered.length}</span>
                </div>
                <div className="absolute bottom-4 right-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); withCard(current, "download"); }}
                    disabled={busy !== ""}
                    data-testid="royal-slideshow-download"
                    className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
                  >
                    {busy === `download-${current.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Card
                  </button>
                </div>
              </div>

              <div className="h-1 w-full bg-cream/10">
                <div className="h-full bg-gold transition-all duration-500" style={{ width: `${((slideIdx + 1) / filtered.length) * 100}%` }} />
              </div>
            </div>
            <p className="text-center text-xs text-cream/40 mt-3">Auto-playing · hover to pause · click a throne to view details</p>
          </Reveal>
        </div>
      )}

      {/* CONTROLS */}
      <div className="max-w-5xl mx-auto px-6 pb-8 space-y-5">
        <Reveal>
          <div className="relative">
            <Search className="w-4 h-4 text-cream/40 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search a town, title or region…"
              data-testid="royal-titles-search-input"
              className="w-full rounded-full bg-night-surface border border-cream/20 pl-12 pr-6 py-3.5 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors"
            />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="flex flex-wrap gap-2">
            {regions.map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                data-testid={`royal-titles-region-${r}`}
                className={`text-sm rounded-full px-5 py-2 border transition-colors ${
                  region === r
                    ? "bg-gold text-night border-gold font-semibold"
                    : "border-cream/20 text-cream/75 hover:border-gold hover:text-gold"
                }`}
              >
                {r === "all" ? "All Regions" : r}
              </button>
            ))}
            <span className="ml-auto self-center text-sm text-cream/45" data-testid="royal-titles-count">
              {filtered.length} {filtered.length === 1 ? "throne" : "thrones"}
            </span>
          </div>
        </Reveal>
      </div>

      {/* LIST */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading royal titles…</p>
        ) : filtered.length === 0 ? (
          <p className="text-cream/50 py-16 text-center" data-testid="royal-titles-empty">
            No thrones match your search yet.
          </p>
        ) : (
          grouped.map((g) => (
            <section key={g.region} className="mb-12" data-testid={`royal-titles-group-${g.region}`}>
              <div className="flex items-center gap-3 mb-5">
                <MapPin className="w-4 h-4 text-gold" />
                <h2 className="font-serif text-2xl text-gold">{g.region}</h2>
                <div className="h-px flex-1 bg-cream/10" />
                <span className="text-xs text-cream/40">{g.items.length}</span>
              </div>
              <div className="grid gap-5 md:grid-cols-2">
                {g.items.map((t) => (
                  <div
                    key={t.id}
                    className="group h-full flex flex-col border border-cream/12 rounded-2xl bg-night-surface overflow-hidden cursor-pointer transition-colors hover:border-gold/50"
                    data-testid={`royal-title-card-${t.id}`}
                    onClick={() => navigate(`/royal-titles/${t.id}`)}
                    role="button"
                    aria-label={`View details for ${t.title}`}
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={t.image || imageFor(t.region)}
                        alt={`${t.title} royal regalia`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,16,14,0.15) 0%, rgba(18,16,14,0.85) 100%)" }} />
                      <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-gold-soft">{t.town}</p>
                        <Crown className="w-5 h-5 text-gold" />
                      </div>
                    </div>
                    <div className="flex flex-col flex-1 p-7">
                      <h3 className="font-serif text-2xl text-gold leading-snug mb-3 group-hover:underline decoration-gold/40 underline-offset-4">{t.title}</h3>
                      {t.note && (
                        <p className="text-cream/70 text-[15px] leading-relaxed mb-5 flex-1 line-clamp-3">{t.note}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-auto pt-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); withCard(t, "download"); }}
                          disabled={busy !== ""}
                          data-testid={`royal-title-download-${t.id}`}
                          className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-4 py-2 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
                        >
                          {busy === `download-${t.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          Card
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); withCard(t, "share"); }}
                          disabled={busy !== ""}
                          data-testid={`royal-title-share-${t.id}`}
                          className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-4 py-2 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                        >
                          {busy === `share-${t.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                          Share
                        </button>
                        <span className="ml-auto self-center inline-flex items-center gap-1 text-gold/80 text-sm font-semibold group-hover:text-gold" data-testid={`royal-title-view-${t.id}`}>
                          View <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {/* Hidden branded share card */}
      {shareTarget && (
        <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
          <div
            ref={cardRef}
            data-testid="royal-title-share-card"
            style={{
              width: 1080,
              height: 1080,
              background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
              color: "#F5EFE6",
              padding: 90,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />

            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>ÀsÀNigHT</div>
              <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>WORLDWIDE</div>
              <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>Yorùbá Royal Titles · Oyè Ọba</div>
            </div>

            <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
              <div style={{ fontSize: 22, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginBottom: 18 }}>{shareTarget.town}</div>
              <div
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  color: "#D4A94A",
                  fontSize: shareTarget.title.length > 34 ? 62 : shareTarget.title.length > 22 ? 80 : 96,
                  lineHeight: 1.08,
                  marginBottom: 30,
                }}
              >
                {shareTarget.title}
              </div>
              {shareTarget.note && (
                <div style={{ fontSize: 30, lineHeight: 1.45, color: "#F5EFE6" }}>{shareTarget.note}</div>
              )}
            </div>

            <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
              {shareTarget.region && (
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>{shareTarget.region}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <Newsletter />

      {/* Slideshow detail modal */}
      <AnimatePresence>
        {modalTarget && (
          <motion.div
            className="fixed inset-0 z-[100] grid place-items-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="royal-modal"
            onClick={() => setModalTarget(null)}
          >
            <div className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
            <motion.div
              className="relative w-full max-w-2xl rounded-3xl overflow-hidden border border-gold/25 bg-night-surface max-h-[90vh] flex flex-col"
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 16 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setModalTarget(null)}
                data-testid="royal-modal-close"
                aria-label="Close"
                className="absolute top-4 right-4 z-10 grid place-items-center w-10 h-10 rounded-full bg-night/60 border border-cream/20 text-cream hover:bg-gold hover:text-night transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="relative h-52 md:h-64 shrink-0">
                <img
                  src={modalTarget.image || imageFor(modalTarget.region)}
                  alt={`${modalTarget.title} royal regalia`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(18,16,14,0.1) 0%, rgba(18,16,14,0.9) 100%)" }} />
                <div className="absolute bottom-5 left-6 right-6">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 text-gold text-xs font-semibold px-3 py-1 mb-3">
                    <MapPin className="w-3.5 h-3.5" /> {modalTarget.region}
                  </span>
                  <p className="uppercase tracking-[0.25em] text-xs text-gold-soft mb-1">{modalTarget.town}</p>
                  <h2 className="font-serif text-2xl md:text-4xl text-gold leading-tight" data-testid="royal-modal-title">{modalTarget.title}</h2>
                </div>
              </div>
              <div className="p-6 md:p-8 overflow-y-auto">
                {modalTarget.note && (
                  <p className="text-cream/80 text-base md:text-lg leading-relaxed mb-6" data-testid="royal-modal-note">{modalTarget.note}</p>
                )}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate(`/royal-titles/${modalTarget.id}`)}
                    data-testid="royal-modal-open-page"
                    className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors"
                  >
                    Open full page <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => withCard(modalTarget, "download")}
                    disabled={busy !== ""}
                    data-testid="royal-modal-download"
                    className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                  >
                    {busy === `download-${modalTarget.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Card
                  </button>
                  <button
                    onClick={() => withCard(modalTarget, "share")}
                    disabled={busy !== ""}
                    data-testid="royal-modal-share"
                    className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                  >
                    {busy === `share-${modalTarget.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                    Share
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
